import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";

import Layout from "./components/Layout";
import Home from "./pages/Home";
import Menu from "./pages/Menu";
import OrderRedirect from "./pages/OrderRedirect";
import Visit from "./pages/Visit";
import Wholesale from "./pages/Wholesale";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Offers from "./pages/Offers";
import Privacy from "./pages/Privacy";
import Cookies from "./pages/Cookies";

import { CrmAuthProvider } from "./crm/CrmAuthContext";
import { SectionGuard } from "./crm/SectionGuard";
import { TeamAuthProvider } from "./team/TeamAuthContext";

// Heavy internal apps (CRM, employee PWA, TV boards, maps, charts) are code-split so
// the public marketing site never downloads them. Big win for mobile LCP/TBT.
const CrmLayout = lazy(() => import("./crm/CrmLayout"));
const CrmLogin = lazy(() => import("./crm/Login"));
const CrmDashboard = lazy(() => import("./crm/Dashboard"));
const CrmAnalytics = lazy(() => import("./crm/Analytics"));
const CrmClientMap = lazy(() => import("./crm/ClientMap"));
const CrmClients = lazy(() => import("./crm/Clients"));
const CrmProducts = lazy(() => import("./crm/Products"));
const CrmIngredients = lazy(() => import("./crm/Ingredients"));
const CrmMenuManager = lazy(() => import("./crm/MenuManager"));
const CrmLeadEngine = lazy(() => import("./crm/LeadEngine"));
const CrmOrders = lazy(() => import("./crm/Orders"));
const CrmOrderForm = lazy(() => import("./crm/OrderForm"));
const CrmProduction = lazy(() => import("./crm/Production"));
const CrmDeliveryNotes = lazy(() => import("./crm/DeliveryNotes"));
const CrmReturns = lazy(() => import("./crm/Returns"));
const CrmStatement = lazy(() => import("./crm/Statement"));
const CrmInvoices = lazy(() => import("./crm/Invoices"));
const CrmHelp = lazy(() => import("./crm/Help"));
const CrmTeam = lazy(() => import("./crm/Team"));
const CrmActivity = lazy(() => import("./crm/Activity"));
const CrmScreenManager = lazy(() => import("./crm/ScreenManager"));
const CrmSettings = lazy(() => import("./crm/Settings"));
const CrmStaffHub = lazy(() => import("./crm/hr/StaffHub"));
const CrmRota = lazy(() => import("./crm/hr/Rota"));
const CrmLeave = lazy(() => import("./crm/hr/Leave"));
const CrmStaffCalendar = lazy(() => import("./crm/hr/StaffCalendar"));
const CrmEmployees = lazy(() => import("./crm/hr/Employees"));
const CrmAttendance = lazy(() => import("./crm/hr/Attendance"));
const CrmStaffSettings = lazy(() => import("./crm/hr/StaffSettings"));
const TeamApp = lazy(() => import("./team/TeamApp"));
const TeamLogin = lazy(() => import("./team/TeamLogin"));
const TeamJoin = lazy(() => import("./team/TeamJoin"));
const MenuBoard = lazy(() => import("./pages/MenuBoard"));

function RouteFallback() {
  return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 28, height: 28, border: "3px solid #e5e1d8", borderTopColor: "#C8102E", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}

export default function App() {
  return (
    <div className="App">
      <div className="noise-overlay" aria-hidden="true" />
      <BrowserRouter>
        <CrmAuthProvider>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route element={<Layout />}>
                <Route path="/" element={<Home />} />
                <Route path="/menu" element={<Menu />} />
                <Route path="/order" element={<OrderRedirect />} />
                <Route path="/visit" element={<Visit />} />
                <Route path="/wholesale" element={<Wholesale />} />
                <Route path="/offers" element={<Offers />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/cookies" element={<Cookies />} />
              </Route>

              {/* Public full-screen in-store TV menu board (no site chrome) */}
              <Route path="/screen/:slug" element={<MenuBoard />} />

              {/* Employee PWA (mobile-first, separate from management CRM) */}
              <Route path="/team/login" element={<TeamAuthProvider><TeamLogin /></TeamAuthProvider>} />
              <Route path="/team/join" element={<TeamJoin />} />
              <Route path="/team/*" element={<TeamAuthProvider><TeamApp /></TeamAuthProvider>} />

              {/* Internal staff CRM */}
              <Route path="/crm/admin/login" element={<CrmLogin />} />
              <Route path="/crm/admin" element={<CrmLayout />}>
                <Route index element={<CrmDashboard />} />
                <Route path="analytics" element={<SectionGuard section="analytics"><CrmAnalytics /></SectionGuard>} />
                <Route path="client-map" element={<SectionGuard section="analytics"><CrmClientMap /></SectionGuard>} />
                <Route path="clients" element={<SectionGuard section="clients"><CrmClients /></SectionGuard>} />
                <Route path="products" element={<SectionGuard section="products"><CrmProducts /></SectionGuard>} />
                <Route path="ingredients" element={<SectionGuard section="ingredients"><CrmIngredients /></SectionGuard>} />
                <Route path="menu" element={<SectionGuard section="menu"><CrmMenuManager /></SectionGuard>} />
                <Route path="leads" element={<SectionGuard section="leads"><CrmLeadEngine /></SectionGuard>} />
                <Route path="orders" element={<SectionGuard section="orders"><CrmOrders /></SectionGuard>} />
                <Route path="orders/new" element={<SectionGuard section="orders"><CrmOrderForm /></SectionGuard>} />
                <Route path="orders/:orderId" element={<SectionGuard section="orders"><CrmOrderForm /></SectionGuard>} />
                <Route path="production" element={<SectionGuard section="production"><CrmProduction /></SectionGuard>} />
                <Route path="delivery-notes" element={<SectionGuard section="orders"><CrmDeliveryNotes /></SectionGuard>} />
                <Route path="returns" element={<SectionGuard section="returns"><CrmReturns /></SectionGuard>} />
                <Route path="invoices" element={<SectionGuard section="invoices"><CrmInvoices /></SectionGuard>} />
                <Route path="statement" element={<SectionGuard section="invoices"><CrmStatement /></SectionGuard>} />
                <Route path="team" element={<SectionGuard section="__admin__"><CrmTeam /></SectionGuard>} />
                <Route path="activity" element={<SectionGuard section="__admin__"><CrmActivity /></SectionGuard>} />
                <Route path="settings" element={<SectionGuard section="__admin__"><CrmSettings /></SectionGuard>} />
                <Route path="screens" element={<SectionGuard section="screens"><CrmScreenManager /></SectionGuard>} />
                <Route path="staff-hub" element={<SectionGuard section="rota"><CrmStaffHub /></SectionGuard>} />
                <Route path="rota" element={<SectionGuard section="rota"><CrmRota /></SectionGuard>} />
                <Route path="leave" element={<SectionGuard section="rota"><CrmLeave /></SectionGuard>} />
                <Route path="staff-calendar" element={<SectionGuard section="rota"><CrmStaffCalendar /></SectionGuard>} />
                <Route path="employees" element={<SectionGuard section="people"><CrmEmployees /></SectionGuard>} />
                <Route path="attendance" element={<SectionGuard section="rota"><CrmAttendance /></SectionGuard>} />
                <Route path="staff-settings" element={<SectionGuard section="people"><CrmStaffSettings /></SectionGuard>} />
                <Route path="help" element={<CrmHelp />} />
              </Route>
            </Routes>
          </Suspense>
        </CrmAuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors closeButton />
    </div>
  );
}
